import { BalanceData, ChestResult, GearItem, LevelSim, LifetimeStats, rareLevelOf, starfallSkins, wyrmArchetype, wyrmReward } from "@highmarch/domain";
import type { LevelCatalog } from "./levelCatalog";

/**
 * The Starfall Wyrm's use cases (M40, docs/RARES.md; domain sim/wyrm.ts, meta/wyrmReward.ts): the reward the moment it
 * is slain, the paid retry when it flies off, and what the presentation shows of it (the Almanac row, the lore).
 *
 * - Reward: granted at once on the kill (WyrmKilled), like a rare's chest (LevelCatalog.bankRareChest): a Starfall skin
 *   the player lacks (owned at once, unseen so the picker marks it new), or with the set complete a Mythic chest and the
 *   gems. Once per Wyrm: keyed by the battle's seed and the Wyrm's stage in the lifetime stats (`stats` section), so a
 *   resumed battle (which replays the kill) or a checkpoint retry grants nothing again — it returns the same grant with
 *   `fresh: false`, for the reveal.
 * - Retry: `retryGems` (2,500 = $25 at the shop's 100 gems per $) from the wallet, logged as "wyrmRetry". The one spend
 *   allowed in battle (the hub-only purchase rule, meta/shop.ts purchasesAllowed, is for the Shop's products). Keyed by
 *   seed, stage and attempt: the decision is charged once, however often it is replayed. The web then issues
 *   `Commands.wyrmRetry()`.
 *
 * The caller saves after either.
 */

export type WyrmGrant =
  | { readonly kind: "skin"; readonly skinId: string; readonly fresh: boolean }
  | { readonly kind: "chest"; readonly chest: ChestResult; readonly gems: number; readonly gear: readonly GearItem[]; readonly fresh: boolean };

/** The stats' note of a complete-set grant (a skin grant notes the skin id). */
const CHEST = "chest";

/** The grant's ledger ref and stats key of the run's Wyrm: the battle's seed and its stage. */
export function wyrmKey(sim: LevelSim): string { return `${sim.seed.toString(16)}:${sim.wyrmRun?.stage ?? 0}`; }

/** A slain Wyrm's reward (see the module note); null when the run's Wyrm has not been slain. */
export function grantWyrmKill(catalog: LevelCatalog, sim: LevelSim, now?: number): WyrmGrant | null {
  const run = sim.wyrmRun;
  if (run === null || run.outcome !== "killed") return null;
  const B = catalog.balance, stats = catalog.achievements.stats, key = wyrmKey(sim), level = rareLevelOf(sim);
  const prior = stats.wyrmGrantOf(key);
  if (prior !== null) {
    if (prior !== CHEST) return { kind: "skin", skinId: prior, fresh: false };
    const r = wyrmReward(B, () => true, level, sim.seed, run.stage);
    return r.kind === "chest" ? { kind: "chest", chest: r.chest, gems: r.gems, gear: [], fresh: false } : null;
  }
  const r = wyrmReward(B, (id) => catalog.ownsSkin(id), level, sim.seed, run.stage);
  if (r.kind === "skin") {
    catalog.unlockSkin(r.skinId);
    stats.noteWyrmGrant(key, r.skinId);
    return { kind: "skin", skinId: r.skinId, fresh: true };
  }
  const gear = catalog.bankChest(r.chest);
  catalog.economy.wallet.grantGems(r.gems, "wyrm", now, `wyrm:${key}`);
  stats.noteWyrmGrant(key, CHEST);
  return { kind: "chest", chest: r.chest, gems: r.gems, gear, fresh: true };
}

/** What another try costs, in gems. */
export function wyrmRetryGems(balance: BalanceData): number { return balance.spawnRules.wyrm.retryGems; }

/**
 * Pays for another try at the Wyrm that just flew off (see the module note): true (paid now, or this attempt was paid
 * already) or why not ("no choice", "not enough gems"). On true the caller issues `Commands.wyrmRetry()`.
 */
export function buyWyrmRetry(catalog: LevelCatalog, sim: LevelSim, now: number): true | string {
  const run = sim.wyrmRun;
  if (run === null || !sim.wyrmChoiceOpen || sim.isOver) return "no choice";
  const key = `${wyrmKey(sim)}:${run.attempts + 1}`, stats = catalog.achievements.stats;
  if (stats.wyrmRetryPaid(key)) return true;
  const r = catalog.economy.wallet.spend("gems", wyrmRetryGems(catalog.balance), "wyrmRetry", now, `wyrm:${key}`);
  if (r !== true) return r;
  stats.noteWyrmRetry(key);
  return true;
}

/** The Wyrm for the presentation: its lore line and its glow (the names live in balance). */
export const WYRM_LOOK = {
  lore: "A wyrm of opal and starlight that falls burning from the night sky. Bring it down and its treasure is yours.",
  sparkle: 0xbfa8ff,
} as const;

/** The Almanac's Wyrm row: seen once it has appeared, battles met, times slain, Starfall skins owned of the set. */
export interface WyrmAlmanacRow {
  readonly id: string;
  readonly name: string;
  readonly lore: string;
  readonly sparkle: number;
  readonly seen: boolean;
  readonly battles: number;
  readonly kills: number;
  readonly skinsOwned: number;
  readonly skinsTotal: number;
}

/** The Wyrm's Almanac row, or null in a bundle without it. `owns`: the cosmetics' (LevelCatalog.ownsSkin). */
export function wyrmAlmanac(balance: BalanceData, stats: LifetimeStats, owns: (skinId: string) => boolean): WyrmAlmanacRow | null {
  const w = wyrmArchetype(balance);
  if (w < 0) return null;
  const e = balance.enemies[w], set = starfallSkins(balance);
  return {
    id: e.id, name: e.name, lore: WYRM_LOOK.lore, sparkle: WYRM_LOOK.sparkle,
    seen: stats.wyrmsSeen > 0 || stats.killsOf(e.id) > 0, battles: stats.wyrmsSeen, kills: stats.wyrmsKilled,
    skinsOwned: set.filter((s) => owns(s.id)).length, skinsTotal: set.length,
  };
}
